import {
  mdiAccountGroup,
  mdiAccountKey,
  mdiCalendar,
  mdiCardAccountDetailsOutline,
  mdiCards,
  mdiCogs,
  mdiCommentMultipleOutline,
  mdiCrownOutline,
  mdiFileDocumentMultiple,
  mdiFileDocumentOutline,
  mdiFilterMultiple,
  mdiFolderMultipleImage,
  mdiGamepadVariant,
  mdiNintendoGameBoy,
  mdiPacMan,
  mdiPublish,
  mdiStorefrontOutline,
  mdiTagMultiple
} from "@mdi/js"
import { Role } from "../graphql/types"

export function getPrivilegeIconAndTitle(privilege: Role | string): { icon: string | null; title: string | null } {
  const icon = getPrivilegeIcon(privilege)
  const title = getPrivilegeTitle(privilege)

  return { icon, title }
}

export const ROLE_ORDER: Role[] = [
  Role.MANAGE_OWN_ARTICLES,
  Role.PUBLISH_ARTICLES,
  Role.MANAGE_ARTICLES,
  Role.DELETE_OWN_ARTICLES,
  Role.DELETE_ARTICLES,
  Role.MANAGE_OWN_ARTICLE_SERIES,
  Role.MANAGE_ARTICLE_SERIES,
  Role.DELETE_OWN_ARTICLE_SERIES,
  Role.DELETE_ARTICLE_SERIES,
  Role.MANAGE_OWN_EVENTS,
  Role.MANAGE_EVENTS,
  Role.DELETE_OWN_EVENTS,
  Role.DELETE_EVENTS,
  Role.MANAGE_OWN_GAMES,
  Role.MANAGE_GAMES,
  Role.DELETE_OWN_GAMES,
  Role.DELETE_GAMES,
  Role.MANAGE_OWN_GAME_SERIES,
  Role.MANAGE_GAME_SERIES,
  Role.DELETE_OWN_GAME_SERIES,
  Role.DELETE_GAME_SERIES,
  Role.MANAGE_OWN_GENRES,
  Role.MANAGE_GENRES,
  Role.DELETE_OWN_GENRES,
  Role.DELETE_GENRES,
  Role.MANAGE_OWN_GAME_CONSOLES,
  Role.MANAGE_GAME_CONSOLES,
  Role.DELETE_OWN_GAME_CONSOLES,
  Role.DELETE_GAME_CONSOLES,
  Role.MANAGE_OWN_COMPANIES,
  Role.MANAGE_COMPANIES,
  Role.DELETE_OWN_COMPANIES,
  Role.DELETE_COMPANIES,
  Role.MANAGE_OWN_MEDIA,
  Role.MANAGE_MEDIA,
  Role.DELETE_OWN_MEDIA,
  Role.DELETE_MEDIA,
  Role.MANAGE_OWN_TAG,
  Role.MANAGE_TAG,
  Role.DELETE_OWN_TAG,
  Role.DELETE_TAG,
  Role.MANAGE_OWN_QUICK_FILTER,
  Role.MANAGE_QUICK_FILTER,
  Role.DELETE_OWN_QUICK_FILTER,
  Role.DELETE_QUICK_FILTER,
  Role.MANAGE_OWN_RATING_CATEGORIES,
  Role.MANAGE_RATING_CATEGORIES,
  Role.DELETE_OWN_RATING_CATEGORIES,
  Role.DELETE_RATING_CATEGORIES,
  Role.MANAGE_OWN_AUTHORS,
  Role.MANAGE_AUTHORS,
  Role.DELETE_OWN_AUTHORS,
  Role.DELETE_AUTHORS,
  Role.MANAGE_USERS,
  Role.MANAGE_USER_ROLES,
  Role.MANAGE_COMMENTS,
  Role.SYSTEM_ADMINISTRATION,
  Role.ACTUATOR
]

export function comparePrivilege(a: Role, b: Role): number {
  const aPrio = ROLE_ORDER.indexOf(a)
  const bPrio = ROLE_ORDER.indexOf(b)
  // Roles not listed in ROLE_ORDER (e.g. the newer DELETE_* roles) sort after
  // all listed ones, alphabetically among themselves — keeps the comparator a
  // consistent total order regardless of which roles Role happens to contain.
  const aRank = -1 === aPrio ? ROLE_ORDER.length : aPrio
  const bRank = -1 === bPrio ? ROLE_ORDER.length : bPrio

  if (aRank === bRank) return a.localeCompare(b)
  else return aRank - bRank
}

export function getPrivilegeIcon(privilege: Role | string): string | null {
  switch (privilege) {
    case Role.MANAGE_OWN_ARTICLES:
      return mdiFileDocumentOutline
    case Role.PUBLISH_ARTICLES:
      return mdiPublish
    case Role.MANAGE_ARTICLES:
      return mdiFileDocumentMultiple
    case Role.MANAGE_ARTICLE_SERIES:
    case Role.MANAGE_OWN_ARTICLE_SERIES:
      return mdiFileDocumentMultiple
    case Role.MANAGE_EVENTS:
    case Role.MANAGE_OWN_EVENTS:
      return mdiCalendar
    case Role.MANAGE_AUTHORS:
    case Role.MANAGE_OWN_AUTHORS:
      return mdiCardAccountDetailsOutline
    case Role.MANAGE_USERS:
      return mdiAccountGroup
    case Role.MANAGE_RATING_CATEGORIES:
    case Role.MANAGE_OWN_RATING_CATEGORIES:
      return mdiCrownOutline
    case Role.MANAGE_GAMES:
    case Role.MANAGE_OWN_GAMES:
      return mdiGamepadVariant
    case Role.MANAGE_GAME_SERIES:
    case Role.MANAGE_OWN_GAME_SERIES:
      return mdiCards
    case Role.MANAGE_GENRES:
    case Role.MANAGE_OWN_GENRES:
      return mdiPacMan
    case Role.MANAGE_GAME_CONSOLES:
    case Role.MANAGE_OWN_GAME_CONSOLES:
      return mdiNintendoGameBoy
    case Role.MANAGE_COMPANIES:
    case Role.MANAGE_OWN_COMPANIES:
      return mdiStorefrontOutline
    case Role.MANAGE_MEDIA:
    case Role.MANAGE_OWN_MEDIA:
      return mdiFolderMultipleImage
    case Role.MANAGE_TAG:
    case Role.MANAGE_OWN_TAG:
      return mdiTagMultiple
    case Role.MANAGE_QUICK_FILTER:
    case Role.MANAGE_OWN_QUICK_FILTER:
      return mdiFilterMultiple
    case Role.MANAGE_COMMENTS:
      return mdiCommentMultipleOutline
    case Role.MANAGE_USER_ROLES:
      return mdiAccountKey
    case Role.SYSTEM_ADMINISTRATION:
      return mdiCogs
    case Role.DELETE_OWN_ARTICLES:
    case Role.DELETE_ARTICLES:
    case Role.DELETE_OWN_ARTICLE_SERIES:
    case Role.DELETE_ARTICLE_SERIES:
      return mdiFileDocumentMultiple
    case Role.DELETE_OWN_EVENTS:
    case Role.DELETE_EVENTS:
      return mdiCalendar
    case Role.DELETE_OWN_AUTHORS:
    case Role.DELETE_AUTHORS:
      return mdiCardAccountDetailsOutline
    case Role.DELETE_OWN_RATING_CATEGORIES:
    case Role.DELETE_RATING_CATEGORIES:
      return mdiCrownOutline
    case Role.DELETE_OWN_GAMES:
    case Role.DELETE_GAMES:
      return mdiGamepadVariant
    case Role.DELETE_OWN_GAME_SERIES:
    case Role.DELETE_GAME_SERIES:
      return mdiCards
    case Role.DELETE_OWN_GENRES:
    case Role.DELETE_GENRES:
      return mdiPacMan
    case Role.DELETE_OWN_GAME_CONSOLES:
    case Role.DELETE_GAME_CONSOLES:
      return mdiNintendoGameBoy
    case Role.DELETE_OWN_COMPANIES:
    case Role.DELETE_COMPANIES:
      return mdiStorefrontOutline
    case Role.DELETE_OWN_MEDIA:
    case Role.DELETE_MEDIA:
      return mdiFolderMultipleImage
    case Role.DELETE_OWN_TAG:
    case Role.DELETE_TAG:
      return mdiTagMultiple
    case Role.DELETE_OWN_QUICK_FILTER:
    case Role.DELETE_QUICK_FILTER:
      return mdiFilterMultiple
    default:
      return null
  }
}

export function getPrivilegeTitle(privilege: Role | string): string | null {
  switch (privilege) {
    case Role.MANAGE_OWN_ARTICLES:
      return "Darf eigene Artikel verwalten"
    case Role.PUBLISH_ARTICLES:
      return "Darf Artikel veröffentlichen"
    case Role.MANAGE_ARTICLES:
      return "Darf Artikel verwalten"
    case Role.MANAGE_ARTICLE_SERIES:
      return "Darf Artikelserien verwalten"
    case Role.MANAGE_OWN_ARTICLE_SERIES:
      return "Darf eigene Artikelserien verwalten"
    case Role.MANAGE_EVENTS:
      return "Darf Events verwalten"
    case Role.MANAGE_OWN_EVENTS:
      return "Darf eigene Events verwalten"
    case Role.MANAGE_AUTHORS:
      return "Darf Autoren verwalten"
    case Role.MANAGE_OWN_AUTHORS:
      return "Darf eigene Autoren verwalten"
    case Role.MANAGE_USERS:
      return "Darf Benutzer einsehen"
    case Role.MANAGE_RATING_CATEGORIES:
      return "Darf Bewertungskriterien verwalten"
    case Role.MANAGE_OWN_RATING_CATEGORIES:
      return "Darf eigene Bewertungskriterien verwalten"
    case Role.MANAGE_GAMES:
      return "Darf Spiele verwalten"
    case Role.MANAGE_OWN_GAMES:
      return "Darf eigene Spiele verwalten"
    case Role.MANAGE_GAME_SERIES:
      return "Darf Spielserien verwalten"
    case Role.MANAGE_OWN_GAME_SERIES:
      return "Darf eigene Spielserien verwalten"
    case Role.MANAGE_GENRES:
      return "Darf Genres verwalten"
    case Role.MANAGE_OWN_GENRES:
      return "Darf eigene Genres verwalten"
    case Role.MANAGE_GAME_CONSOLES:
      return "Darf Konsolen verwalten"
    case Role.MANAGE_OWN_GAME_CONSOLES:
      return "Darf eigene Konsolen verwalten"
    case Role.MANAGE_COMPANIES:
      return "Darf Unternehmen verwalten"
    case Role.MANAGE_OWN_COMPANIES:
      return "Darf eigene Unternehmen verwalten"
    case Role.MANAGE_MEDIA:
      return "Darf Medien verwalten"
    case Role.MANAGE_OWN_MEDIA:
      return "Darf eigene Medien verwalten"
    case Role.MANAGE_TAG:
      return "Darf Schlagworte / Tags verwalten"
    case Role.MANAGE_OWN_TAG:
      return "Darf eigene Schlagworte / Tags verwalten"
    case Role.MANAGE_QUICK_FILTER:
      return "Darf Schnellfilter verwalten"
    case Role.MANAGE_OWN_QUICK_FILTER:
      return "Darf eigene Schnellfilter verwalten"
    case Role.MANAGE_COMMENTS:
      return "Darf Kommentare moderieren"
    case Role.MANAGE_USER_ROLES:
      return "Darf Benutzerrollen verwalten"
    case Role.SYSTEM_ADMINISTRATION:
      return "Darf System verwalten"
    case Role.ACTUATOR:
      return "Darf Actuator-Endpunkte aufrufen"
    case Role.DELETE_ARTICLES:
      return "Darf Artikel löschen"
    case Role.DELETE_OWN_ARTICLES:
      return "Darf eigene Artikel löschen"
    case Role.DELETE_ARTICLE_SERIES:
      return "Darf Artikelserien löschen"
    case Role.DELETE_OWN_ARTICLE_SERIES:
      return "Darf eigene Artikelserien löschen"
    case Role.DELETE_AUTHORS:
      return "Darf Autoren löschen"
    case Role.DELETE_OWN_AUTHORS:
      return "Darf eigene Autoren löschen"
    case Role.DELETE_COMPANIES:
      return "Darf Unternehmen löschen"
    case Role.DELETE_OWN_COMPANIES:
      return "Darf eigene Unternehmen löschen"
    case Role.DELETE_EVENTS:
      return "Darf Events löschen"
    case Role.DELETE_OWN_EVENTS:
      return "Darf eigene Events löschen"
    case Role.DELETE_GAMES:
      return "Darf Spiele löschen"
    case Role.DELETE_OWN_GAMES:
      return "Darf eigene Spiele löschen"
    case Role.DELETE_GAME_CONSOLES:
      return "Darf Konsolen löschen"
    case Role.DELETE_OWN_GAME_CONSOLES:
      return "Darf eigene Konsolen löschen"
    case Role.DELETE_GAME_SERIES:
      return "Darf Spielserien löschen"
    case Role.DELETE_OWN_GAME_SERIES:
      return "Darf eigene Spielserien löschen"
    case Role.DELETE_GENRES:
      return "Darf Genres löschen"
    case Role.DELETE_OWN_GENRES:
      return "Darf eigene Genres löschen"
    case Role.DELETE_MEDIA:
      return "Darf Medien löschen"
    case Role.DELETE_OWN_MEDIA:
      return "Darf eigene Medien löschen"
    case Role.DELETE_QUICK_FILTER:
      return "Darf Schnellfilter löschen"
    case Role.DELETE_OWN_QUICK_FILTER:
      return "Darf eigene Schnellfilter löschen"
    case Role.DELETE_RATING_CATEGORIES:
      return "Darf Bewertungskriterien löschen"
    case Role.DELETE_OWN_RATING_CATEGORIES:
      return "Darf eigene Bewertungskriterien löschen"
    case Role.DELETE_TAG:
      return "Darf Schlagworte / Tags löschen"
    case Role.DELETE_OWN_TAG:
      return "Darf eigene Schlagworte / Tags löschen"
    default:
      return null
  }
}
